import {
  Banner,
  Button,
  ButtonText,
  Input,
  Modal,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import FormFieldSelect from './FormField/FormFieldSelect';
import { FieldLabel } from 'src/components/Field';
import { FormProvider, useForm } from 'react-hook-form';
import { allPhaseMap } from '@publishing-engine/domain/models/workflow-phase-ownership';
import { FormFieldSelect as IFormFieldSelect } from '@publishing-engine/domain/types/article';
import {
  fetchSubmissionContext,
  fetchWorkspaceByShortname,
  fetchWorkspaceJournals,
  getArticleWorkflowSubmissionStates
} from 'src/api/queries';
import { useMutation, useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import axiosInstance from 'src/api/client';
import { useNavigate } from 'react-router-dom';

interface IProps {
  workspace: {
    workspaceId: string;
    shortname: string;
    name: string;
  };
}

type IFormPayload = {
  shortname: string;
  journal: string;
  state: string;
  articleType: string;
  submittingAuthorEmail?: string;
};

const useSeedTestSubmission = () => {
  const mutation = useMutation({
    mutationFn: ({
      shortname,
      submittingAuthorEmail,
      ...rest
    }: IFormPayload) => {
      const payload = submittingAuthorEmail
        ? { ...rest, submittingAuthorEmail }
        : rest;
      return axiosInstance
        .post(`/dev/workspaces/${shortname}/submissions`, payload)
        .then(r => ({
          ...r.data,
          isSuccess: true as const
        }))
        .catch(e => ({
          message: e.response.data,
          isSuccess: false as const
        }));
    }
  });

  return mutation;
};

const getBaseConfig = (name: string) => ({
  name,
  component: 'Custom:Select',
  params: {
    labels: {
      main: name,
      subtitle: ''
    },
    validations: {
      kind: 'options',
      required: true
    }
  },
  caseDataFields: {
    main: name
  }
});

const getJournalConfig = (
  journals: { id: string; data: { name: string } }[]
) => {
  const journalOptions = journals?.reduce(
    (acc, value) => {
      acc.labelOptions[value.id] = {
        main: value.data.name
      };
      acc.validationOptions.push(value.id);
      return acc;
    },
    {
      labelOptions: {},
      validationOptions: []
    } as {
      labelOptions: Record<string, { main: string }>;
      validationOptions: string[];
    }
  );

  return _.merge(getBaseConfig('journal'), {
    params: {
      labels: {
        main: 'Journal',
        subtitle: 'Select the desired journal you want the submission to be in',
        options: journalOptions.labelOptions
      },
      validations: {
        options: journalOptions.validationOptions
      }
    }
  }) as IFormFieldSelect;
};

const getArticleTypeConfig = (
  articleTypes: { name: string; articleWorkflowId: string }[]
) => {
  const journalOptions = articleTypes?.reduce(
    (acc, value) => {
      acc.labelOptions[value.articleWorkflowId] = {
        main: value.name
      };
      acc.validationOptions.push(value.articleWorkflowId);
      return acc;
    },
    {
      labelOptions: {},
      validationOptions: []
    } as {
      labelOptions: Record<string, { main: string }>;
      validationOptions: string[];
    }
  );

  return _.merge(getBaseConfig('articleType'), {
    params: {
      labels: {
        main: 'Article Type',
        subtitle:
          'Select the desired article type you want the submission to be in',
        options: journalOptions.labelOptions
      },
      validations: {
        options: journalOptions.validationOptions
      }
    }
  }) as IFormFieldSelect;
};

const getStateConfig = (states: string[] = []) => {
  const options = states?.reduce(
    (acc, value) => {
      acc.labelOptions[value] = {
        main: allPhaseMap[value as keyof typeof allPhaseMap] ?? value
      };
      acc.validationOptions.push(value);
      return acc;
    },
    {
      labelOptions: {},
      validationOptions: []
    } as {
      labelOptions: Record<string, { main: string }>;
      validationOptions: string[];
    }
  );

  return _.merge(getBaseConfig('state'), {
    params: {
      labels: {
        main: 'Submission State',
        subtitle: 'Select the desired state you want the submission to be in',
        options: options.labelOptions
      },
      validations: {
        options: options.validationOptions
      }
    }
  }) as IFormFieldSelect;
};

export const SubmissionSeeder = ({ workspace }: IProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [submission, setSubmission] = useState<{
    id: string;
    shortname: string;
    submissionContextId: string;
  }>();
  const seedTestSubmissionMutation = useSeedTestSubmission();

  const methods = useForm<IFormPayload>({
    defaultValues: {
      shortname: workspace.shortname,
      journal: '',
      articleType: '',
      state: '',
      submittingAuthorEmail: ''
    }
  });

  const { handleSubmit, watch, reset, resetField, register } = methods;

  const { shortname, journal, articleType } = watch();

  useEffect(() => {
    if (journal) resetField('articleType');
  }, [journal]);

  useEffect(() => {
    if (articleType) resetField('state');
  }, [articleType]);

  const { data: workspaceData } = useQuery({
    queryKey: ['workspace', { shortname: workspace.shortname }],
    queryFn: () => fetchWorkspaceByShortname(workspace.shortname),
    enabled: !!workspace.shortname
  });

  const { data: journalData } = useQuery({
    queryKey: ['workspace-journals', { workspaceId: workspaceData?.id }],
    queryFn: () =>
      workspaceData?.id ? fetchWorkspaceJournals(workspaceData?.id) : null
  });

  const { data: submissionStateData } = useQuery({
    queryKey: ['submission-context', journal],
    queryFn: () => fetchSubmissionContext(journal),
    enabled: !!journal
  });

  const { data: stateData } = useQuery({
    queryKey: ['article-workflow-states', { articleType }],
    queryFn: () => getArticleWorkflowSubmissionStates(articleType),
    enabled: !!articleType
  });

  const onClose = () => setIsOpen(false);

  const onSubmit = async (values: IFormPayload) => {
    const res = await seedTestSubmissionMutation.mutateAsync(values);

    if (res.isSuccess && res.submissionId) {
      setSubmission({
        id: res.submissionId,
        shortname,
        submissionContextId: res.submissionContextId
      });
      reset();
    } else {
      toast({
        variant: 'error',
        title: 'Error Creating Submission in Desired State',
        description:
          res?.message?.error ?? res?.message ?? 'Not supported yet!',
        isClosable: true
      });
    }
  };

  const isEnabled = shortname && journal && articleType;

  return (
    <>
      <ButtonText
        iconLeft={<FontAwesomeIcon icon={faPlus} />}
        onClick={() => setIsOpen(true)}
      >
        Add Submission
      </ButtonText>
      <Modal
        isOpen={isOpen}
        onClose={() => {}}
        width="720px"
        closeButtonProps={{
          hidden: true
        }}
      >
        {submission ? (
          <Flex
            padding={8}
            gap={6}
            bgColor="surfacePrimaryFill"
            flexDirection="column"
            borderRadius="base"
          >
            <Text textStyle="headingSection" textAlign={'center'}>
              Submission Created
            </Text>
            <Text textStyle="headingGroup" textAlign={'center'}>
              Submission ID: {submission.id}
            </Text>
            <Flex alignSelf={'center'}>
              <ButtonText
                onClick={() => {
                  onClose();
                  navigate(
                    `/app/workspace/${submission.shortname}/j/${submission.submissionContextId}/submissions?selectedItem=${submission.id}`
                  );
                }}
              >
                View Submission
              </ButtonText>
            </Flex>
            <Flex
              padding={6}
              bottom={0}
              justifyContent="center"
              position="sticky"
              bgColor="surfacePrimaryFill"
            >
              <Flex width="100%" justifyContent="flex-end" gap={4}>
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
                <Button
                  variant="primary"
                  onClick={() => setSubmission(undefined)}
                >
                  Add Another
                </Button>
              </Flex>
            </Flex>
          </Flex>
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Flex
                padding={8}
                gap={6}
                bgColor="surfacePrimaryFill"
                flexDirection="column"
                borderRadius="base"
              >
                <Flex flexDirection="column" gap={2}>
                  <Text textStyle="headingGroup">Add a Submission</Text>
                  <Banner
                    content={
                      <Text>
                        By submitting this form, you will be creating a new
                        submission for <strong>A.Matthew Lewis</strong> using a
                        set of system defined, pre-generated data.
                      </Text>
                    }
                    heading="Attention - Convenience Feature!"
                    variant="attention"
                  />
                </Flex>

                {!!journalData?.collection.length && (
                  <FormFieldSelect
                    config={getJournalConfig(journalData.collection)}
                  />
                )}

                {journal && !!submissionStateData?.articleTypes.length && (
                  <FormFieldSelect
                    config={getArticleTypeConfig(
                      submissionStateData.articleTypes
                    )}
                  />
                )}

                {articleType && !!stateData?.states.length && (
                  <FormFieldSelect config={getStateConfig(stateData.states)} />
                )}

                <Flex
                  flexDirection="column"
                  alignItems="flex-start"
                  gap={2}
                  width="100%"
                >
                  <FieldLabel
                    label="Submitting Author Email"
                    subText="Optional — defaults to the first seeded author if left blank"
                    isRequired={false}
                  />
                  <Input
                    {...register('submittingAuthorEmail')}
                    type="email"
                    placeholder="lewis.scup.ixh4joki@mailosaur.io"
                  />
                </Flex>
              </Flex>
              <Flex
                padding={6}
                bottom={0}
                justifyContent="center"
                position="sticky"
                bgColor="surfacePrimaryFill"
              >
                <Button
                  variant="secondary"
                  onClick={() => reset()}
                  isDisabled={seedTestSubmissionMutation.isLoading}
                >
                  Reset Fields
                </Button>
                <Flex width="100%" justifyContent="flex-end" gap={4}>
                  <Button
                    variant="secondary"
                    onClick={onClose}
                    isDisabled={seedTestSubmissionMutation.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    isDisabled={
                      !isEnabled || seedTestSubmissionMutation.isLoading
                    }
                    leftIcon={
                      seedTestSubmissionMutation.isLoading ? (
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                        />
                      ) : undefined
                    }
                  >
                    {seedTestSubmissionMutation.isLoading
                      ? 'Creating...'
                      : 'Add Submission'}
                  </Button>
                </Flex>
              </Flex>
            </form>
          </FormProvider>
        )}
      </Modal>
    </>
  );
};
